import { Link, useNavigate } from "@tanstack/react-router";
import { Flame } from "lucide-react";
import { useState } from "react";
import { type BrewDetail, send, useBrew, useChange } from "../api";
import { Dialog, DialogForm, ErrorNote, Field } from "../components/ui";
import { showDate } from "../format";
import { useNotify } from "../notify";
import { type Bar, brewName, type DropQuestion } from "../tankSchedule";
import { BrewTanks, TakeOffDialog } from "./BrewTanks";
import { beerName } from "./Repitch";

/**
 * Asked before a drop that makes a double batch or books a hidden tank (Tyler, 2026-10-05: "if we
 * didn't mean to combine them the brew just goes back to wherever it started on that drag").
 */
export function AskDialog({
  q,
  bar,
  onYes,
  onNo,
}: {
  q: DropQuestion;
  bar: Bar;
  onYes: () => void;
  onNo: () => void;
}) {
  const days = q.to ? `${showDate(q.from)} to ${showDate(q.to)}` : `from ${showDate(q.from)}`;
  const combine = q.combines.length > 0;
  const [brew] = bar.brews;
  const named = brew ? brewName(brew) : bar.label;
  const name = named.charAt(0).toUpperCase() + named.slice(1);
  return (
    <Dialog
      open
      onClose={onNo}
      title={combine ? "Combine into a double batch?" : `Book ${q.tank.name}? It is hidden`}
    >
      <DialogForm
        onSubmit={onYes}
        footer={
          <>
            <button type="button" className="button" onClick={onNo}>
              Put it back
            </button>
            <button type="submit" className="button primary">
              {combine ? "Combine them" : `Book ${q.tank.name}`}
            </button>
          </>
        }
      >
        {combine ? (
          <p style={{ margin: 0 }}>
            {name} would share {q.tank.name} with {q.combines.map(brewName).join(" and ")}, {days},
            {q.combines.length === 1 ? " as one double batch." : " all in it together."}
          </p>
        ) : (
          <p style={{ margin: 0 }}>
            {name} would go in {q.tank.name}, {days}.
          </p>
        )}
        {q.hidden ? (
          <p className="muted" style={{ margin: 0 }}>
            {q.tank.name} is hidden from the tank schedule for everyone at the brewery, and stays
            hidden.
          </p>
        ) : null}
        <p className="faint" style={{ margin: 0 }}>
          Put it back leaves everything where it was before you dragged it.
        </p>
      </DialogForm>
    </Dialog>
  );
}
/**
 * A bar clicked: its brew's tanks, to change, move or empty, and its brew day while it is planned. A
 * brew not started can be taken off the calendar from here (Scott's Jam, 2026-10-08: "there needs to
 * be an option to unschedule or cancel this brew on the screen for scheduling tanks"); taking only a
 * booking off, the brew staying planned, is its booking's Change.
 *
 * A planned brew's brew day starts from here too, and opens (Scott's Jam, 2026-10-08: "a begin brew
 * or start brew day button should exist when clicking the recipe. its location on top is not
 * effective").
 */
export function BrewPanel({ brewId, onClose }: { brewId: string; onClose: () => void }) {
  const { data: b, error } = useBrew(brewId);
  const [takingOff, setTakingOff] = useState(false);
  const navigate = useNavigate();
  const notify = useNotify();
  const start = useChange((_: undefined, key) => send.startBrewDay(brewId, key), {
    onSuccess: (brew) => {
      notify({
        title: `Brew day started for ${brew.recipe.name}`,
        link: { kind: "brew", brewId: brew.id },
      });
      navigate({ to: "/brews/$brewId", params: { brewId: brew.id } });
    },
  });
  const title = b ? beerName({ recipeName: b.recipe.name, number: b.number }) : "Brew";
  return (
    <Dialog open onClose={onClose} title={title}>
      <div className="dialog-body ts-panel">
        {b ? (
          <>
            <p className="muted" style={{ margin: 0 }}>
              {b.status === "brewed"
                ? `Brewed ${showDate(b.brewedOn)}`
                : b.status === "draft"
                  ? `Brew day started ${showDate(b.brewedOn)}`
                  : `Planned for ${showDate(b.brewedOn)}`}
              .{" "}
              <Link to="/brews/$brewId" params={{ brewId: b.id }}>
                Open the brew
              </Link>
            </p>
            {b.status === "planned" ? <BrewDay brew={b} /> : null}
            <BrewTanks brew={b} />
          </>
        ) : (
          <p className="muted">Loading</p>
        )}
        <ErrorNote error={error ?? start.error} />
      </div>
      <div className="dialog-footer ts-panel-footer">
        {b?.status === "planned" ? (
          <button
            type="button"
            className="button danger"
            onClick={() => setTakingOff(true)}
            style={{ marginRight: "auto" }}
          >
            Take it off the calendar
          </button>
        ) : null}
        <button type="button" className="button" onClick={onClose}>
          Done
        </button>
        {b?.status === "planned" ? (
          <button
            type="button"
            className="button primary"
            disabled={start.isPending}
            onClick={() => start.mutate(undefined)}
          >
            <Flame size={16} /> Start brew day
          </button>
        ) : null}
      </div>
      {takingOff ? (
        <TakeOffDialog brewId={brewId} onClose={() => setTakingOff(false)} onDone={onClose} />
      ) : null}
    </Dialog>
  );
}
/** A planned brew moved to another day, its tank bookings with it. */
function BrewDay({ brew: b }: { brew: BrewDetail }) {
  const [on, setOn] = useState(b.brewedOn);
  const save = useChange((day: string, key) =>
    send.saveBrew({ id: b.id, patch: { brewedOn: day } }, key),
  );
  return (
    <>
      <div className="ts-brew-day">
        <Field label="Brew day">
          <input
            className="input"
            type="date"
            value={on}
            onChange={(e) => e.target.value && setOn(e.target.value)}
          />
        </Field>
        <button
          type="button"
          className="button"
          disabled={on === b.brewedOn || save.isPending}
          onClick={() => save.mutate(on)}
        >
          Move it
        </button>
      </div>
      <p className="faint" style={{ margin: "-6px 0 0" }}>
        Its tank bookings move with it.
      </p>
      <ErrorNote error={save.error} />
    </>
  );
}
